/**
 * Payroll — Payroll management page (bulletins, declarations, Pajemploi).
 *
 * Refactored: Card JSX and modal extracted into components/payroll/.
 * This shell manages state, API calls, and orchestrates the sub-components.
 */
import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Calculator, Calendar, Crown, Lock, Euro, History, FileText,
  CheckCircle, Clock, AlertTriangle, XCircle, FileCheck, Send, Baby, Loader2
} from 'lucide-react';
import { ChildFilterCards } from '../components/ChildFilterCards';
import { genderAccent } from '../components/ChildIcon';
import { toast } from 'sonner';
import { API_BASE as API } from '../utils/constants';
import { PayrollCard, DeclarationCard, DeclarationModal, PajemploiOnboardingGuide } from '../components/payroll';

const Payroll = () => {
  const { user, isPremium, isParent } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // ── State ──
  const [payrollDrafts, setPayrollDrafts] = useState([]);
  const [declarations, setDeclarations] = useState([]);
  const [months, setMonths] = useState([]);
  const [selectedMonth, setSelectedMonth] = useState(null);
  const [selectedChild, setSelectedChild] = useState('all');
  const [loading, setLoading] = useState(true);
  const [premiumRequired, setPremiumRequired] = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState(null);
  const [downloadingCra, setDownloadingCra] = useState(null);

  // Declaration modal state
  const [declarationModalOpen, setDeclarationModalOpen] = useState(false);
  const [selectedDraft, setSelectedDraft] = useState(null);
  const [pajemploiStep, setPajemploiStep] = useState('prepare');
  const [checkingReadiness, setCheckingReadiness] = useState(false);
  const [readiness, setReadiness] = useState(null);
  const [estimation, setEstimation] = useState(null);
  const [predeclaration, setPredeclaration] = useState(null);
  const [estimating, setEstimating] = useState(false);
  const [predeclaring, setPredeclaring] = useState(false);
  const [predeclarationFailed, setPredeclarationFailed] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Preparation state (new proactive flow)
  const [preparing, setPreparing] = useState(false);
  const [preparationSteps, setPreparationSteps] = useState([]);
  const [preparationSuccess, setPreparationSuccess] = useState(false);

  // Onboarding guide state (Pajemploi consent)
  const [onboardingOpen, setOnboardingOpen] = useState(false);
  const [consentSubmitting, setConsentSubmitting] = useState(false);
  const [hasConsent, setHasConsent] = useState(false);
  const [pendingDraft, setPendingDraft] = useState(null);

  // ── Fetch Pajemploi consent status ──
  useEffect(() => {
    if (!isParent) return;
    const fetchConsent = async () => {
      try {
        const res = await axios.get(`${API}/profile`);
        setHasConsent(!!res.data.pajemploi_mandate_consent);
      } catch (err) {
        console.warn('Payroll: failed to fetch consent status', err);
      }
    };
    fetchConsent();
  }, [isParent]);

  // ── Data fetching ──
  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const monthParam = selectedMonth && selectedMonth !== 'all' ? `?month=${selectedMonth}` : '';
      const [payrollRes, declRes] = await Promise.all([
        axios.get(`${API}/payroll/drafts${monthParam}`),
        axios.get(`${API}/payroll/declarations/history${monthParam}`),
      ]);
      setPayrollDrafts(payrollRes.data);
      setDeclarations(declRes.data);
      // Build months list from drafts data
      const monthSet = new Set();
      payrollRes.data.forEach(d => monthSet.add(d.month));
      declRes.data.forEach(d => monthSet.add(d.month));
      const monthList = Array.from(monthSet).sort().reverse().map(m => ({
        value: m,
        label: new Date(m + '-01').toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' }).replace(/^\w/, c => c.toUpperCase()),
      }));
      setMonths(monthList);
      setPremiumRequired(false);
    } catch (error) {
      if (error.response?.status === 403) {
        setPremiumRequired(true);
      } else {
        toast.error('Erreur lors du chargement de la paie');
      }
    } finally {
      setLoading(false);
    }
  }, [selectedMonth]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleMonthChange = (val) => setSelectedMonth(val === 'all' ? null : val);

  // ── Declaration modal handlers ──
  const openDeclarationModal = async (draft) => {
    // Block if email not verified
    if (user?.email_verified === false) {
      toast.error('Veuillez vérifier votre adresse email avant de déclarer.', {
        description: 'Rendez-vous dans votre profil pour renvoyer le lien de vérification.',
      });
      return;
    }
    // If parent hasn't given Pajemploi consent yet, show onboarding first
    if (isParent && !hasConsent) {
      setPendingDraft(draft);
      setOnboardingOpen(true);
      return;
    }
    _openDeclarationFlow(draft);
  };

  const _openDeclarationFlow = async (draft) => {
    setSelectedDraft(draft);
    setDeclarationModalOpen(true);
    setPajemploiStep('prepare');
    setReadiness(null);
    setEstimation(null);
    setPredeclaration(null);
    setPredeclarationFailed(false);
    setPreparationSteps([]);
    setPreparationSuccess(false);
    setPreparing(true);

    try {
      const res = await axios.post(`${API}/payroll/pajemploi/prepare/${draft.contract_id}`);
      setPreparationSteps(res.data.steps || []);
      if (res.data.success) {
        setPreparationSuccess(true);
      } else {
        setPreparationSuccess(false);
      }
    } catch (error) {
      const errMsg = error?.response?.data?.detail || "Erreur lors de la préparation Pajemploi";
      setPreparationSteps([{ step: 'error', label: 'Erreur', success: false, message: errMsg }]);
      setPreparationSuccess(false);
    } finally {
      setPreparing(false);
    }
  };

  const runEstimation = async () => {
    if (!selectedDraft) return;
    setPajemploiStep('estimate');
    setEstimating(true);
    try {
      const res = await axios.post(`${API}/payroll/pajemploi/estimate`, { contract_id: selectedDraft.contract_id, month: selectedDraft.month });
      setEstimation(res.data);
      setPajemploiStep('predeclare');
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de l'estimation");
      // Still advance to predeclare so user can attempt
      setPajemploiStep('predeclare');
    } finally { setEstimating(false); }
  };

  const runPredeclaration = async () => {
    if (!selectedDraft) return;
    setPredeclaring(true);
    try {
      const res = await axios.post(`${API}/payroll/pajemploi/predeclare`, { contract_id: selectedDraft.contract_id, month: selectedDraft.month });
      setPredeclaration(res.data);
      if (res.data.success) { setPajemploiStep('confirm'); }
      else {
        toast.error(res.data.error || 'Erreur lors de la pré-déclaration');
        // Mark predeclaration as failed so user can skip to local declaration
        setPredeclarationFailed(true);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la pré-déclaration');
    } finally { setPredeclaring(false); }
  };

  const submitDeclaration = async () => {
    if (!selectedDraft) return;
    setSubmitting(true);
    // If predeclaration failed, force local declaration
    const forceLocal = predeclarationFailed;
    try {
      const res = await axios.post(`${API}/payroll/pajemploi/declare`, { contract_id: selectedDraft.contract_id, month: selectedDraft.month, force_local: forceLocal });
      const data = res.data;
      if (data.status === 'submitted' || data.status === 'confirmed') {
        toast.success(`Déclaration soumise ! Réf: ${data.reference_number}`);
      } else if (data.status === 'local_only') {
        toast.info(data.message || 'Déclaration enregistrée localement');
      } else {
        toast.warning(data.message || 'Déclaration enregistrée avec un avertissement');
      }
      setDeclarationModalOpen(false);
      setSelectedDraft(null);
      await fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la soumission');
    } finally { setSubmitting(false); }
  };

  // ── PDF downloads ──
  const downloadPdf = async (contractId, month, childName) => {
    setDownloadingPdf(`${contractId}-${month}`);
    try {
      const response = await axios.get(`${API}/payroll/pdf/${contractId}/${month}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = `bulletin_paie_${childName}_${month}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success('Bulletin de paie téléchargé');
    } catch { toast.error('Erreur lors de la génération du PDF'); }
    finally { setDownloadingPdf(null); }
  };

  const downloadCra = async (contractId, month, childName) => {
    setDownloadingCra(`${contractId}-${month}`);
    try {
      const response = await axios.get(`${API}/payroll/cra/${contractId}/${month}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = `CRA_${childName}_${month}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success('CRA téléchargé');
    } catch { toast.error('Erreur lors de la génération du CRA'); }
    finally { setDownloadingCra(null); }
  };

  // ── Helpers ──

  // ── Pajemploi Onboarding consent handler ──
  const handleOnboardingConsent = async () => {
    setConsentSubmitting(true);
    try {
      await axios.post(`${API}/profile/pajemploi-consent`);
      setHasConsent(true);
      setOnboardingOpen(false);
      toast.success('Consentement Pajemploi enregistré');
      // If there was a pending draft, open the declaration modal now
      if (pendingDraft) {
        _openDeclarationFlow(pendingDraft);
        setPendingDraft(null);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'enregistrement du consentement');
    } finally {
      setConsentSubmitting(false);
    }
  };

  const getDeclarationForDraft = (draft) => declarations.find(d => d.contract_id === draft.contract_id && d.month === draft.month);

  const getStatusBadge = (status) => {
    const config = {
      submitted: { label: 'Soumise', icon: CheckCircle, className: 'bg-[var(--success)] text-white' },
      confirmed: { label: 'Confirmée', icon: CheckCircle, className: 'bg-[var(--success)] text-white' },
      pending: { label: 'En attente', icon: Clock, className: 'bg-[var(--warning-light)] text-[var(--warning)]' },
      network_error: { label: 'Erreur réseau', icon: AlertTriangle, className: 'bg-[var(--secondary-100)] text-[var(--secondary-600)]' },
      local_only: { label: 'Enregistrée localement', icon: FileCheck, className: 'bg-[var(--primary-100)] text-[var(--primary-600)]' },
      error: { label: 'Erreur', icon: XCircle, className: 'bg-[var(--error-light)] text-[var(--error)]' },
      validation_error: { label: 'Erreur validation', icon: XCircle, className: 'bg-[var(--error-light)] text-[var(--error)]' },
    };
    const c = config[status] || config.pending;
    const Icon = c.icon;
    return <Badge className={`${c.className} gap-1 text-xs`}><Icon className="h-3 w-3" />{c.label}</Badge>;
  };

  // ── Child filter logic ──
  const children = (() => {
    const map = new Map();
    payrollDrafts.forEach(d => {
      if (!map.has(d.contract_id)) {
        map.set(d.contract_id, {
          contract_id: d.contract_id,
          child_name: d.child_name,
          child_first_name: d.child_first_name || d.child_name,
          child_sex: d.child_sex,
          count: 0,
        });
      }
      map.get(d.contract_id).count += 1;
    });
    return Array.from(map.values());
  })();

  const filteredDrafts = selectedChild === 'all'
    ? payrollDrafts
    : payrollDrafts.filter(d => d.contract_id === selectedChild);

  const filteredDeclarations = selectedChild === 'all'
    ? declarations
    : declarations.filter(d => d.contract_id === selectedChild);

  // ── Loading state ──
  if (loading) {
    return (
      <div className="container-custom py-6 space-y-6" data-testid="payroll-loading">
        <Skeleton className="h-10 w-64" />
        <div className="grid gap-4"><Skeleton className="h-48 w-full" /><Skeleton className="h-48 w-full" /></div>
      </div>
    );
  }

  // ── Premium gate ──
  if (premiumRequired) {
    return (
      <div className="container-custom py-6 space-y-6" data-testid="payroll-premium-required">
        <div>
          <h1 className="text-3xl font-bold" style={{ fontFamily: 'var(--font-secondary)', color: 'var(--text-primary)' }}>Paie & Déclarations</h1>
          <p className="text-[var(--text-secondary)] mt-1">Bulletins de salaire et déclarations Pajemploi</p>
        </div>
        <Card className="border-2 border-[var(--secondary-200)] bg-[var(--secondary-50)]/50">
          <CardContent className="flex flex-col items-center text-center py-12 px-6 space-y-6">
            <div className="w-16 h-16 rounded-full bg-[var(--secondary-100)] flex items-center justify-center">
              <Lock className="h-8 w-8 text-[var(--secondary-400)]" />
            </div>
            <div className="space-y-2 max-w-md">
              <h2 className="text-2xl font-bold" style={{ fontFamily: 'var(--font-secondary)', color: 'var(--text-primary)' }}>Passez à la version complète</h2>
              <p className="text-[var(--text-secondary)]">La gestion de la paie, les bulletins de salaire PDF et les déclarations Pajemploi/Urssaf nécessitent un abonnement. Un seul abonnement suffit pour en faire profiter toute votre collaboration.</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-sm w-full max-w-lg">
              {['Bulletins de salaire PDF', 'Déclarations Pajemploi', 'Calcul des cotisations'].map(label => (
                <div key={label} className="flex items-center gap-2 p-3 rounded-lg bg-[var(--bg-primary)] border border-[var(--secondary-200)]">
                  <CheckCircle className="h-4 w-4 text-[var(--secondary-400)] shrink-0" />
                  <span className="text-[var(--text-secondary)]">{label}</span>
                </div>
              ))}
            </div>
            <Button onClick={() => navigate('/billing')} className="h-12 px-8 text-lg font-semibold" style={{ backgroundColor: 'var(--secondary-400)' }} data-testid="payroll-upgrade-btn">
              <Crown className="mr-2 h-5 w-5" /> Passer à la version complète
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // ── Main render ──
  return (
    <div className="container-custom py-6 space-y-6" data-testid="payroll-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold" style={{ color: 'var(--text-primary)' }}>
            <Calculator className="inline h-7 w-7 mr-2 text-[var(--primary-400)]" /> Gestion de la Paie
          </h1>
          <p className="text-sm mt-1" style={{ color: 'var(--text-secondary)' }}>
            {isParent ? 'Consultez, déclarez et téléchargez les bulletins de paie' : 'Consultez vos bulletins de paie'}
          </p>
        </div>
        <Select value={selectedMonth || 'all'} onValueChange={handleMonthChange}>
          <SelectTrigger className="w-[200px]" data-testid="month-select">
            <Calendar className="h-4 w-4 mr-2 text-[var(--primary-400)]" />
            <SelectValue placeholder="Tous les mois" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous les mois</SelectItem>
            {months.map(m => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {/* Child filter cards — shared component, same style as TimeValidation */}
      {children.length > 1 && (
        <ChildFilterCards
          children={children}
          selectedChild={selectedChild}
          onSelect={setSelectedChild}
          allLabel="Tous les enfants"
          allSublabel={`${payrollDrafts.length} bulletin(s)`}
          renderStats={(child) => (
            <>
              <span className="text-xs font-mono font-semibold" style={{ color: genderAccent(child.child_sex) }}>
                {child.count} bulletin(s)
              </span>
            </>
          )}
        />
      )}

      {/* Tabs */}
      <Tabs defaultValue="payroll" className="w-full">
        <TabsList className="grid w-full grid-cols-2 max-w-md h-auto p-1" style={{ backgroundColor: 'var(--neutral-100)' }}>
          <TabsTrigger value="payroll" data-testid="tab-payroll" className="gap-2 text-sm py-2 data-[state=active]:bg-[var(--bg-primary)] data-[state=active]:text-[var(--primary-400)] data-[state=active]:shadow-sm">
            <Euro className="h-4 w-4" /> Bulletins de paie
          </TabsTrigger>
          <TabsTrigger value="history" data-testid="tab-history" className="gap-2 text-sm py-2 data-[state=active]:bg-[var(--bg-primary)] data-[state=active]:text-[var(--primary-400)] data-[state=active]:shadow-sm">
            <History className="h-4 w-4" /> Déclarations
          </TabsTrigger>
        </TabsList>

        {/* === PAYROLL TAB === */}
        <TabsContent value="payroll" className="space-y-4 mt-4">
          {filteredDrafts.length === 0 ? (
            <Card className="border-dashed">
              <CardContent className="flex flex-col items-center justify-center py-12">
                <FileText className="h-12 w-12 text-[var(--text-muted)] mb-4" />
                <h3 className="text-lg font-medium" style={{ color: 'var(--text-primary)' }}>Aucune donnée de paie</h3>
                <p className="text-sm mt-1 text-center max-w-md" style={{ color: 'var(--text-secondary)' }}>
                  Les bulletins de paie apparaîtront ici une fois les heures enregistrées et validées.
                </p>
              </CardContent>
            </Card>
          ) : (
            filteredDrafts.map((draft) => {
              const existingDecl = getDeclarationForDraft(draft);
              const isDeclared = existingDecl && ['submitted', 'confirmed', 'local_only'].includes(existingDecl.status);
              return (
                <PayrollCard
                  key={`${draft.contract_id}-${draft.month}`}
                  draft={draft}
                  existingDecl={existingDecl}
                  isDeclared={isDeclared}
                  isParent={isParent}
                  downloadingPdf={downloadingPdf}
                  downloadingCra={downloadingCra}
                  FileCheckIcon={FileCheck}
                  onDownloadPdf={downloadPdf}
                  onDownloadCra={downloadCra}
                  onDeclare={openDeclarationModal}
                  getStatusBadge={getStatusBadge}
                />
              );
            })
          )}
        </TabsContent>

        {/* === DECLARATIONS HISTORY TAB === */}
        <TabsContent value="history" className="space-y-4 mt-4">
          {filteredDeclarations.length === 0 ? (
            <Card className="border-dashed">
              <CardContent className="flex flex-col items-center justify-center py-12">
                <FileCheck className="h-12 w-12 text-[var(--text-muted)] mb-4" />
                <h3 className="text-lg font-medium" style={{ color: 'var(--text-primary)' }}>Aucune déclaration</h3>
                <p className="text-sm mt-1 text-center max-w-md" style={{ color: 'var(--text-secondary)' }}>
                  {isParent ? 'Vos déclarations Pajemploi apparaîtront ici.' : 'Les déclarations de votre employeur apparaîtront ici.'}
                </p>
              </CardContent>
            </Card>
          ) : (
            filteredDeclarations.map((decl) => (
              <DeclarationCard key={decl.id} decl={decl} getStatusBadge={getStatusBadge} onRetrySuccess={fetchData} />
            ))
          )}
        </TabsContent>
      </Tabs>

      {/* === PAJEMPLOI ONBOARDING GUIDE === */}
      <PajemploiOnboardingGuide
        open={onboardingOpen}
        onOpenChange={(open) => {
          setOnboardingOpen(open);
          if (!open) setPendingDraft(null);
        }}
        onConsent={handleOnboardingConsent}
        submitting={consentSubmitting}
      />

      {/* === PAJEMPLOI DECLARATION MODAL === */}
      <DeclarationModal
        open={declarationModalOpen}
        onOpenChange={setDeclarationModalOpen}
        selectedDraft={selectedDraft}
        pajemploiStep={pajemploiStep}
        preparing={preparing}
        preparationSteps={preparationSteps}
        preparationSuccess={preparationSuccess}
        checkingReadiness={checkingReadiness}
        readiness={readiness}
        estimation={estimation}
        predeclaration={predeclaration}
        predeclarationFailed={predeclarationFailed}
        estimating={estimating}
        predeclaring={predeclaring}
        submitting={submitting}
        onEstimate={runEstimation}
        onPredeclare={runPredeclaration}
        onSubmit={submitDeclaration}
        onSkipPredeclare={() => setPajemploiStep('confirm')}
        onGoToProfile={() => { setDeclarationModalOpen(false); navigate('/profil'); }}
      />
    </div>
  );
};

export default Payroll;
